import { NJSegmentedControl, NJSegmentedControlButton, NJTab, NJButton, NJToastService, NJToastContainer } from '@engie-group/fluid-design-system-react';
import React, { useState, useEffect, useMemo } from 'react';
import { Flex } from "antd";
import './Dashboard.css';
import HeaderMenu from './components/HeaderMenu';
import InsightsPopup from './InsightsPopup';
import { useOktaAuth } from "@okta/okta-react";
import { useUser } from './context/UserContext';
import { apiGatewayConfig, maxJobDescriptionLength } from './config';
import { invokeLambda, displayNetworkError } from './api';
import { useNavigate } from 'react-router-dom';
import JobAdCard from './components/JobAdCard';
import JobAdList from './components/JobAdList';
import ModalDeleteJobAd from './components/ModalDeleteJobAd';
import ModalRenameJobAd from './components/ModalRenameJobAd';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import FooterEngie from './components/Footer'
import InputSearch from './components/InputSearch'
import { Input } from 'antd';
import { useReferentials } from './context/ReferentialsContext';

const { TextArea } = Input;

function Dashboard() {

    const { referentials } = useReferentials();
    const userInfo = useUser();
    const { oktaAuth } = useOktaAuth();
    const navigate = useNavigate();

    const [jobDescription, setJobDescription] = useState('');
    const [isOpenInsightsPopup, setIsOpenInsightsPopup] = useState(false);
    const [insightsOptions, setInsightsOptions] = useState({});
    const [isOpenDeleteJobAdModal, setIsOpenDeleteJobAdModal] = useState(false);
    const [isOpenRenameJobAdModal, setIsOpenRenameJobAdModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(false);
    const [toRenameId, setToRenameId] = useState(false);
    const [toRenameTitle, setToRenameTitle] = useState('');
    const [jobAdList, setJobAdList] = useState([]);
    const [templateList, setTemplateList] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [filter, setFilter] = useState('');
    const [needReloadLists, setNeedReloadLists] = useState(false);
    const [listMode, setListMode] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [charCount, setCharCount] = useState(0);


    useEffect(() => {

        if ((window.location.hostname === 'localhost')) {
            setIsAdmin(true);
        } else {
            let username = userInfo?.preferred_username;
            if (username && referentials?.admin && userInfo) {
                let gid = username.split('@')[0].toUpperCase();
                setIsAdmin(referentials.admin.includes(gid));
            }
        }
    }, [referentials, userInfo]);

    // Get user and template list
    useEffect(() => {

        const handleGetJobAdList = async (user) => {
            // Prepare request
            const request = {
                user: user,
            }
            try {
                const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdInfoList, request, userInfo);
                const statusCode = response.statusCode;
                if (statusCode === 200) {
                    setJobAdList(response.userList);
                    setTemplateList(response.templateList)
                } else {
                    console.error("Error:", response.body);
                    alert(response.body);
                }
            } catch (error) {
                console.error('Error invoking Lambda:', error);
                displayNetworkError(error);
            }
        };

        if (userInfo) {
            let email = userInfo.username || userInfo.email;
            let user = email.split('@')[0].toUpperCase();
            handleGetJobAdList(user);
        }
        setNeedReloadLists(false);

    }, [userInfo, needReloadLists, oktaAuth]);

    const createJobAd = () => {
        setInsightsOptions({})
        setIsOpenInsightsPopup(true);

    }

    const handleValidateNewJobAd = (options) => {
        setIsOpenInsightsPopup(false);
        setInsightsOptions(options);

        navigate('/generator', {
            state: {
                action: 'newjobad',
                options,
                jobDescription,
            }
        });
    }

    const openCard = (id, isTemplate, editMode) => {
        let info = currentList.find(item => item.id === id);
        if (info) {
            navigate('/generator', {
                state: {
                    action: isTemplate ? (editMode ? 'editTemplate' : 'useTemplate') : 'jobad',
                    jobAdInfo: info
                }
            });
        }
    }

    function cardActionHandler(props) {
        switch (props.action) {
            case 'delete':
                setIdToDelete(props.id);
                setIsOpenDeleteJobAdModal(true);
                break;
            case 'duplicate':
                handleJobAdDuplicate(props.id);
                break;
            case 'edit':
                openCard(props.id, showTemplates, true);
                break;
            case 'rename':
                setToRenameId(props.id);
                // Get title
                const title = jobAdList.find(item => item.id === props.id)?.title || templateList.find(item => item.id === props.id)?.title;
                setToRenameTitle(title);
                setIsOpenRenameJobAdModal(true);
                break;
            default: break;
        }
    }

    const handleJobAdDuplicate = async (id) => {
        // Get job ad info
        const jobAdInfo = jobAdList.find(item => item.id === id) || templateList.find(item => item.id === id);
        if (jobAdInfo) {
            let newJobAdInfo = { ...jobAdInfo };
            newJobAdInfo.id = null;
            newJobAdInfo.title = `Copy of ${jobAdInfo.title}`;

            // Prepare request
            const request = newJobAdInfo;

            try {
                await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdInfoCreateUpdate, request, userInfo);
                NJToastService('Your job ad has been duplicated!', { iconName: 'check', shouldDismiss: true, dismissAfter: 4000 })
            } catch (error) {
                console.error('Error invoking Lambda:', error);
                displayNetworkError(error);
            }
            // Reload list
            setNeedReloadLists(true);
        }
    }

    const renameJobAd = async (id, newTitle) => {

        const jobAdInfo = jobAdList.find(item => item.id === id) || templateList.find(item => item.id === id);
        if (jobAdInfo) {
            // Prepare request
            const request = {
                id: id,
                title: newTitle,
            };

            try {
                await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdInfoCreateUpdate, request, userInfo);
            } catch (error) {
                console.error('Error invoking Lambda:', error);
                displayNetworkError(error);
            }
            // Reload list
            setNeedReloadLists(true);
        }
    }

    function deleteJobAd(id) {

        const handleJobAdInfoDelete = async (id) => {
            // Prepare request
            const request = {
                id
            }
            try {
                await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdInfoDelete, request, userInfo);
            } catch (error) {
                console.error('Error invoking Lambda:', error);
                displayNetworkError(error);
            }
        };

        // Remove from list
        setJobAdList(jobAdList.filter(job => job.id !== id));
        setTemplateList(templateList.filter(job => job.id !== id));
        // Remove from db
        handleJobAdInfoDelete(id);
    }

    const currentList = useMemo(() => {
        let list = showTemplates ? templateList : jobAdList;
        if (filter) {
            list = list.filter(item =>
                item.title?.toLowerCase().includes(filter.toLowerCase()) ||
                item.subTitle?.toLowerCase().includes(filter.toLowerCase())
            );
        }
        // Sort list 
        list.sort((a, b) => new Date(b.modificationDate) - new Date(a.modificationDate));

        return list || [];
    }, [jobAdList, templateList, showTemplates, filter])

    const isNewUser = useMemo(() => {
        return jobAdList?.length === 0 && templateList?.length > 0;
    }, [jobAdList, templateList])

    useEffect(() => {
        if (jobAdList?.length === 0 && templateList?.length > 0) {
            setShowTemplates(true);
        }
    }, [jobAdList, templateList])

    return (
        <>
            <NJToastContainer type="full-width" />
            <ModalDeleteJobAd
                isOpen={isOpenDeleteJobAdModal}
                id={idToDelete}
                handleCancel={() => { setIsOpenDeleteJobAdModal(false) }}
                handleDelete={(id) => { setIsOpenDeleteJobAdModal(false); deleteJobAd(id) }}
            />
            <ModalRenameJobAd
                isOpen={isOpenRenameJobAdModal}
                title={toRenameTitle}
                id={toRenameId}
                handleCancel={() => { setIsOpenRenameJobAdModal(false) }}
                handleRename={(id, newTitle) => { setIsOpenRenameJobAdModal(false); renameJobAd(id, newTitle) }}
            />
            <InsightsPopup
                isOpen={isOpenInsightsPopup}
                handleClose={() => setIsOpenInsightsPopup(false)}
                handleValidate={handleValidateNewJobAd}
                insightsOptions={insightsOptions}
            />

            <div className='sizelimit-outer'>
                <div className='sizelimit-inner sizelimit-max-width'>
                    <div className="dashboard-outer">
                        <div className="dashboard-inner sizelimit-max-width">
                            <div className="dashboard-container">
                                <Flex vertical={true} justify='space-between' style={{ height: '100%' }}>
                                    <div className="dashboard-header">
                                        <HeaderMenu
                                            username={userInfo?.name}
                                        />
                                    </div>

                                    <div className='dashboard-job-box'>
                                        <div className='dashboard-job-title'>
                                            {isNewUser &&
                                                <span className='dashboard-job-title-newuser'>
                                                    Hello new user,&nbsp;
                                                </span>
                                            }
                                            Welcome to Job Ad Generator!
                                        </div>
                                        <div className='dashboard-job-info'>
                                            Insert here any job elements: job summary, qualifications…or a draft of your job ad
                                        </div>
                                        <div className='dashboard-job-input'>
                                            { /*  <NJFormItem
                                                className='dashboard-job-input-hide-label'
                                                label="" // "Job description"
                                                name="myInput"
                                                isMultiline={true}
                                                onChange={(e) => setJobDescription(e.target.value)}   // onChange event
                                                value={jobDescription} // value
                                                labelKind="static"
                                                placeholder='Do not enter any personal, sensitive, or confidential information'
                                            /> */}
                                            <TextArea
                                                className='dashboard-job-textarea'
                                                placeholder='Do not enter any personal, sensitive, or confidential information'
                                                onChange={(e) => {
                                                    setJobDescription(e.target.value);
                                                    setCharCount(e.target.value.length);
                                                }}
                                                value={jobDescription}
                                                rows={4}
                                                maxLength={maxJobDescriptionLength}
                                            />
                                            <div className={`dashboard-char-counter ${charCount > (maxJobDescriptionLength*.9) ? (charCount >= maxJobDescriptionLength ? 'at-limit' : 'near-limit') : ''
                                                }`}>
                                                {charCount}/{maxJobDescriptionLength} characters
                                            </div>
                                        </div>
                                        <div className='dashboard-job-button'>
                                            <NJButton
                                                label="Create a job ad"
                                                disabled={jobDescription === ''}
                                                onClick={() => createJobAd()}
                                                emphasis="bold"
                                                size="medium"
                                                variant="inverse"
                                            />
                                        </div>
                                    </div>


                                    <div className='nj-tab'>
                                        <div className='nj-tab__items dashboard-tab-items' role='tablist'>
                                            <NJTab
                                                id="user"
                                                label="My Job Ads"
                                                isActive={!showTemplates}
                                                isDisabled={jobAdList?.length < 1}
                                                onClick={() => setShowTemplates(false)}
                                            />
                                            <NJTab
                                                id="template"
                                                label="Templates"
                                                isActive={showTemplates}
                                                isDisabled={templateList?.length < 1}
                                                onClick={() => setShowTemplates(true)}
                                            />
                                        </div>
                                    </div>

                                    <div className='dashboard-bottom'>

                                        <Flex justify='space-between' gap={0} align='center' >
                                            <div>
                                                <InputSearch
                                                    className="dashboard-choices-search"
                                                    id="searchId"
                                                    label="Search"
                                                    placeholder="Search"
                                                    scale='md'
                                                    onChange={(e) => { setFilter(e.currentTarget.value) }}
                                                    onClear={(e) => { setFilter('') }}
                                                />
                                            </div>
                                            <div>
                                                <NJSegmentedControl
                                                    label="Display mode"
                                                    value={listMode ? 'list' : 'card'}
                                                    onChange={(value) => setListMode(value === 'list')}
                                                >
                                                    <NJSegmentedControlButton
                                                        label="Cards"
                                                        value="card"
                                                    />
                                                    <NJSegmentedControlButton
                                                        label="List"
                                                        value="list"
                                                    />
                                                </NJSegmentedControl>
                                            </div>
                                        </Flex>

                                        {listMode && <>
                                            <div className='dashboard-jobadlist-wrapper'>
                                                <JobAdList
                                                    dataSource={currentList}    // data
                                                    actionsHandler={cardActionHandler}
                                                    clickCardHandler={(id) => openCard(id, showTemplates, false)}
                                                    showMenu={!showTemplates || isAdmin}
                                                />

                                            </div>

                                        </>}

                                        {!listMode && <>
                                            <div className='dashboard-choices-cards'>
                                                {/*   <Space direction='horizontal' size={12} wrap={true}> */}
                                                <TransitionGroup className="cards-grid">
                                                    {currentList.map(item => {
                                                        return (
                                                            <CSSTransition
                                                                key={item.id}
                                                                timeout={500}
                                                                classNames="card"
                                                            >
                                                                <div className="card-wrapper" >
                                                                    <JobAdCard key={item.id}
                                                                        id={item.id}
                                                                        title={item.title}
                                                                        subTitle={item?.options?.spc || 'Undefined Global Employee Level'}
                                                                        date={item.modificationDate}
                                                                        actionsHandler={cardActionHandler}
                                                                        clickCardHandler={() => openCard(item.id, showTemplates, false)}
                                                                        showMenu={!showTemplates || isAdmin}
                                                                    />
                                                                </div>
                                                            </CSSTransition>
                                                        )
                                                    })
                                                    }
                                                </TransitionGroup>
                                                { /*    </Space> */}
                                            </div>
                                        </>}

                                    </div>
                                    <FooterEngie dashboard />
                                </Flex>
                            </div>
                        </div>
                    </div >
                </div >
            </div >
        </>
    )
}

export default Dashboard;
